import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as supplierService from "../../services/supplierService";
import { SupplierItem } from "../../interfaces/Supplier";
import { SuppliersResponse } from "./types";

interface SupplierState {
    items: SupplierItem[];
    selectedSupplier: SupplierItem | null;
    loading: boolean;
    error: string | null;
    totalCount: number;
}

const initialState: SupplierState = {
    items: [],
    selectedSupplier: null,
    loading: false,
    error: null,
    totalCount: 0,
};

// Async thunk to fetch suppliers from API
export const fetchSuppliers = createAsyncThunk(
    "suppliers/fetchSuppliers",
    async ({ activeOnly }: { activeOnly?: boolean } = {}) => {
        const response = await supplierService.getSuppliers(undefined, activeOnly);
        // Convert array to SuppliersResponse format
        return {
            items: response,
            totalCount: response.length,
        } as SuppliersResponse;
    }
);

// Async thunk to fetch supplier by ID
export const fetchSupplierById = createAsyncThunk(
    "suppliers/fetchSupplierById",
    async (id: string) => {
        const response = await supplierService.getSupplierById(id);
        return response;
    }
);

// Async thunk to create a new supplier
export const createSupplier = createAsyncThunk(
    "suppliers/createSupplier",
    async (data: supplierService.SupplierCreatePayload) => {
        const response = await supplierService.createSupplier(data);
        return response;
    }
);

// Async thunk to update an existing supplier
export const updateSupplier = createAsyncThunk(
    "suppliers/updateSupplier",
    async ({ id, data }: { id: string; data: supplierService.SupplierUpdatePayload }) => {
        const response = await supplierService.updateSupplier(id, data);
        return response;
    }
);

// Async thunk to delete a supplier
export const deleteSupplier = createAsyncThunk(
    "suppliers/deleteSupplier",
    async (id: string) => {
        await supplierService.deleteSupplier(id);
        return id;
    }
);

const supplierSlice = createSlice({
    name: "Suppliers",
    initialState,
    reducers: {
        selectSupplier(state, action: PayloadAction<string>) {
            state.selectedSupplier =
                state.items.find((s) => s.id === action.payload) || null;
        },
        clearError(state) {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch suppliers
            .addCase(fetchSuppliers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSuppliers.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.items;
                state.totalCount = action.payload.totalCount;
            })
            .addCase(fetchSuppliers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || "Failed to fetch suppliers";
            })
            // Fetch supplier by ID
            .addCase(fetchSupplierById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSupplierById.fulfilled, (state, action) => {
                state.loading = false;
                const supplier = action.payload;
                const index = state.items.findIndex((s) => s.id === supplier.id);
                const supplierItem: SupplierItem = {
                    ...supplier,
                    isDeleted: supplier.isDeleted ?? false,
                };
                if (index !== -1) {
                    state.items[index] = supplierItem;
                } else {
                    state.items.push(supplierItem);
                }
                state.selectedSupplier = supplierItem;
            })
            .addCase(fetchSupplierById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || "Failed to fetch supplier";
            })
            // Create supplier
            .addCase(createSupplier.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createSupplier.fulfilled, (state, action) => {
                state.loading = false;
                const supplier = action.payload;
                const supplierItem: SupplierItem = {
                    ...supplier,
                    isDeleted: supplier.isDeleted ?? false,
                };
                state.items.push(supplierItem);
                state.totalCount += 1;
                state.selectedSupplier = supplierItem;
            })
            .addCase(createSupplier.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || "Failed to create supplier";
            })
            // Update supplier
            .addCase(updateSupplier.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateSupplier.fulfilled, (state, action) => {
                state.loading = false;
                const supplier = action.payload;
                const supplierItem: SupplierItem = {
                    ...supplier,
                    isDeleted: supplier.isDeleted ?? false,
                };
                const index = state.items.findIndex((s) => s.id === supplier.id);
                if (index !== -1) {
                    state.items[index] = supplierItem;
                }
                state.selectedSupplier = supplierItem;
            })
            .addCase(updateSupplier.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || "Failed to update supplier";
            })
            // Delete supplier
            .addCase(deleteSupplier.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteSupplier.fulfilled, (state, action) => {
                state.loading = false;
                state.items = state.items.filter((s) => s.id !== action.payload);
                if (state.selectedSupplier?.id === action.payload) {
                    state.selectedSupplier = null;
                }
                state.totalCount -= 1;
            })
            .addCase(deleteSupplier.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string || "Failed to delete supplier";
            });
    },
});

export const { selectSupplier, clearError } = supplierSlice.actions;

export const selectSupplierList = (state: any) => state.Suppliers.items;
export const selectSupplierById = (state: any, id: string) =>
    state.Suppliers.items.find((s: SupplierItem) => s.id === id);
export const selectSelectedSupplier = (state: any) => state.Suppliers.selectedSupplier;
export const selectSupplierLoading = (state: any) => state.Suppliers.loading;
export const selectSupplierError = (state: any) => state.Suppliers.error;

export default supplierSlice.reducer;
